import moment from 'moment';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { NoRecordFound, STableLoader } from '../../../shared_elements';

const PBHFlexiHD = [
  { key: 'range_condition', label: 'Range Condition', align: 'left' },
  { key: 'min_value', label: 'MIN', align: 'left' },
  { key: 'max_value', label: 'MAX', align: 'right' },
  { key: 'rate_type', label: 'Rate Type', align: 'right' },
  { key: 'rate', label: 'Rate', align: 'right' },
];

const rangeConditionTable = {
  1: 'Less Than Equal To',
  2: 'In Between or Equal To',
  3: 'Greater Than Equal To'
}

const PBHRentTypes = {
  1: 'Flight Hours',
  2: 'Flight Cycles',
  3: 'Block Hours',
  4: 'Flight Hours',
}

const getCurrencyCode = (currency) => {
  if (typeof currency === 'string') {
    return currency || 'USD';
  }

  return currency?.code || currency?.currency_code || currency?.currencyCode || 'USD';
};

const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: getCurrencyCode(currency),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const VariableRentTable = ({ title, rows = [], currency }) => {
  return (
    <>
      {title ? <Typography className='section-header-text'>{title}</Typography> : null}
      <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }} className="scrollable-table rental-scrollable-table-container">
        <Table size="small" className="mui-table-format" stickyHeader>
          <TableHead>
            <TableRow>
              {PBHFlexiHD.map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.length > 0 ? rows.map((row, index) => (
              <TableRow key={row?.id ?? row?.month ?? index} hover>
                <TableCell > {rangeConditionTable[row?.range_condition?.value || row?.range_condition ] || "--"} </TableCell>
                <TableCell > {row?.min_value || "0"} </TableCell>
                <TableCell > {row?.max_value || "∞"} </TableCell>
                <TableCell > {row?.rate_type ? row?.rate_type === 1 ? "FIXED" : row?.rate_type === 2 ? 'PER UNIT' : row?.rate_type?.label || "--" : "--"} </TableCell>
                <TableCell > {formatCurrency(row?.rate, currency)} </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={PBHFlexiHD.length}>
                  <NoRecordFound />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const RatioRateHD = [
  { key: 'ratio', label: 'Ratio', align: 'left' },
  { key: 'rate', label: 'Rate', align: 'right' },
];

const RatioRateTable = ({ title, rows = [], currency }) => (
  <>
    {title ? <Typography className='section-header-text'>{title}</Typography> : null}
    <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }} className="scrollable-table rental-scrollable-table-container">
      <Table size="small" className="mui-table-format" stickyHeader>
        <TableHead>
          <TableRow>
            {RatioRateHD.map((column) => (
              <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.length > 0 ? rows.map((row, index) => (
            <TableRow key={row?.id ?? index} hover>
              <TableCell>{row?.ratio ?? '--'}</TableCell>
              <TableCell>{formatCurrency(row?.rate, currency)}</TableCell>
            </TableRow>
          )) : (
            <TableRow>
              <TableCell colSpan={RatioRateHD.length}>
                <NoRecordFound />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

const CalcPeriodHD = [
  { key: 'calculation_period', label: 'Calculation Period', align: 'left' },
  { key: 'payment_date', label: 'Payment Date', align: 'left' },
  { key: 'rent', label: 'Rent', align: 'right' },
  { key: 'basic_rent', label: 'Basic Rent', align: 'right' },
  { key: 'reference_value', label: 'Reference Value', align: 'right' },
];

const CalcPeriodTable = ({ title, rows = [], currency }) => (
  <>
    {title ? <Typography className='section-header-text'>{title}</Typography> : null}
    <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }} className="scrollable-table rental-scrollable-table-container">
      <Table size="small" className="mui-table-format" stickyHeader>
        <TableHead>
          <TableRow>
            {CalcPeriodHD.map((column) => (
              <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.length > 0 ? rows.map((row, index) => (
            <TableRow key={row?.id ?? index} hover>
              <TableCell>{row?.period ?? '--'}</TableCell>
              <TableCell>{row?.payment_date ? moment(row.payment_date).format('MMM DD, YYYY') : '--'}</TableCell>
              <TableCell>{formatCurrency(row?.rent, currency)}</TableCell>
              <TableCell>{formatCurrency(row?.basic_rent, currency)}</TableCell>
              <TableCell>{row?.reference_value ?? '--'}</TableCell>
            </TableRow>
          )) : (
            <TableRow>
              <TableCell colSpan={CalcPeriodHD.length}>
                <NoRecordFound />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

const VariableRentModal = ({
  open = true,
  onToggleModal = () => { },
  handleItemClicked = () => { },
  skeletonLoader = false,
  variableRent = {},
}) => {
  const data = variableRent?.data;
  const currency = data?.basic_rent_type?.currency;
  const isPBHWhichever = (data?.basic_rent_type?.value === 3 && data?.basic_unit_of_measurement === 4) || null;
  const PBHWhicheverFH = isPBHWhichever ? data?.basic_rent?.filter(option => option?.unit_of_measure_for === 1) : [];
  const PBHWhicheverFC = isPBHWhichever ? data?.basic_rent?.filter(option => option?.unit_of_measure_for === 2) : [];
  return (
    <Dialog
      aria-labelledby="customized-dialog-title"
      className='add-lease-dialog'
      open={open}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ mb: 0, p:'10px 18px' }} className='dialog-header' id="customized-dialog-title">
        Variable Rent
      </DialogTitle>
      <DialogContent dividers>
        {skeletonLoader ? (<STableLoader count={10} rowCount={3} />) : (<>
          {
            data?.basic_rent_type?.value === 3 ? <>
              <VariableRentTable title={PBHRentTypes[data?.basic_unit_of_measurement] || ""} rows={isPBHWhichever ? PBHWhicheverFH : data?.basic_rent || []} currency={currency} />
              {isPBHWhichever ? <div style={{ marginTop: '8px' }}> <VariableRentTable title='Flight Cycles' rows={PBHWhicheverFC} currency={currency} /></div> : null}
            </> : null
          }
          {data?.basic_rent_type?.value === 4 ? data?.basic_rental_rate_based_on === 9 ? <RatioRateTable title='Ratio | Derate' rows={data?.basic_rent || []} currency={currency} />
          : data?.basic_rental_rate_based_on === 8 && <VariableRentTable title='Ratio | Derate' rows={data?.basic_rent || []} currency={currency} /> : null}
          {data?.basic_rent_type?.value === 2 ? <CalcPeriodTable title='Custom (with Payment Schedule)' rows={data?.basic_rent || []} currency={currency} /> : null}

        </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: 1, justifyContent: 'flex-end', marginRight: '15px' }}>
        <Button className='cancel-btn' size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button'); }} variant='outlined' > Close </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariableRentModal;
